import React from "react";
import { X, Loader2 } from "lucide-react";

export default function InventoryCreateModal({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  categories,
  warehousePos,
  handlePoSelect,
  handlePoItemSelect,
  loadingPoItems,
  selectedPoItems,
  warehouses,
  zones,
  loading
}) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Register Inventory SKU</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="sku">SKU Code</label>
              <input
                id="sku"
                type="text"
                required
                className="form-input"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="category">Category</label>
              <select
                id="category"
                className="form-input"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="poId">Connected PO</label>
              <select
                id="poId"
                required
                className="form-input"
                value={formData.poId}
                onChange={(e) => handlePoSelect(e.target.value)}
              >
                <option value="" disabled>Select PO...</option>
                {warehousePos.map(po => (
                  <option key={po.id} value={po.id}>
                    {po.poNumber} {po.clientName ? `(${po.clientName})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="poItemId">PO Line Item</label>
              <select
                id="poItemId"
                required
                className="form-input"
                value={formData.poItemId}
                onChange={(e) => handlePoItemSelect(e.target.value)}
                disabled={loadingPoItems || selectedPoItems.length === 0}
              >
                <option value="" disabled>
                  {loadingPoItems ? "Loading items..." : selectedPoItems.length === 0 ? "Select an PO first..." : "Select line item..."}
                </option>
                {selectedPoItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.description} (Qty: {item.quantity})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label className="form-label" htmlFor="name">Item Title / Name</label>
            <textarea
              id="name"
              required
              className="form-input"
              placeholder="Item description"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              style={{
                resize: "none",
                overflowY: "hidden",
                minHeight: "38px",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineHeight: "1.4",
                padding: "0.5rem 0.75rem"
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = `${el.scrollHeight}px`;
                }
              }}
            />
          </div>

          <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1.5rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="warehouse">Warehouse Assignment</label>
              <select
                id="warehouse"
                className="form-input"
                value={formData.warehouse}
                onChange={(e) => setFormData({ ...formData, warehouse: e.target.value })}
              >
                {warehouses.map(wh => (
                  <option key={wh} value={wh}>{wh}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="zone">Zone Assignment</label>
              <select
                id="zone"
                className="form-input"
                value={formData.zone || ""}
                onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
              >
                <option value="">Unassigned</option>
                {zones && zones.filter(z => !z.isOutline).map(z => (
                  <option key={z.id} value={z.name}>{z.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1.5rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="quantity">Stock Count (In Warehouse)</label>
              <input
                id="quantity"
                type="number"
                required
                className="form-input"
                min="0"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                onWheel={(e) => e.target.blur()}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="orderCount">Order Count (PO Quantity)</label>
              <input
                id="orderCount"
                type="number"
                required
                className="form-input"
                min="0"
                value={formData.orderCount}
                onChange={(e) => setFormData({ ...formData, orderCount: e.target.value })}
                onWheel={(e) => e.target.blur()}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : null}
              Register Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
